import { TypeaheadField } from '@flos/react-ui';
import CoverableDetailsTemplate from '../../../../templates/CoverableDetailsTemplate';
import WizardPagetemplate from '../../../../templates/WizardPagetemplate';
import { useHippoText } from '../../../../hooks/useHippoText';
import { useRecoilValue } from 'recoil';
import { getAccEmploymentsSelector } from '../../../../recoil/selectors/getAccEmploymentsSelector';
import { allCoverablesOnsaAtom } from '../../../../recoil/atoms/allCoverablesOnsaAtom';
import { getOnsaCurrentProductSelector } from '../../../../recoil/selectors/getOnsaCurrentProductSelector';
import { getCoverableIndex } from '../../../../utils/coverablesUtil';
import { useState } from 'react';
import { useSaveCoverableInformation } from '../../../../hooks/useSaveCoverableInformation';
import { type CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import CallMeModal from '../../../../shared_components/CallMeModal/CallMeModal';
import { useUpdateCoverableInformation } from '../../../../recoil/hooks/useUpdateCoverableInformation';
import { isRequestPendingAtom } from '../../../../recoil/atoms/isRequestPendingAtom';
import { track } from '../../../../utils/trackingUtil';
import HelpLink from '../../../../shared_components/HelpLink/HelpLink';

const EmploymentStep = ({
    goNext,
    goPrevious
}: {
    goNext: () => void;
    goPrevious: () => void;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const isRequestPending = useRecoilValue(isRequestPendingAtom);
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const { fixedId, flavourName } = currentProduct;
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const accEmployments = useRecoilValue(getAccEmploymentsSelector);
    const accEmploymentsNames = accEmployments.map((employment) => employment.name).sort();
    const { adultAccidents = [] } = coverables;
    const coverableIndex = getCoverableIndex(adultAccidents, fixedId);
    const coverable = adultAccidents[coverableIndex];
    const { publicId, firstName, selectedEmployment, insuranceForOtherPerson } = coverable;

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isValid, setIsValid] = useState<boolean>(false);
    const [showCallMeModal, setShowCallMeModal] = useState<boolean>(false);
    const [shouldShowHazardousEmploymentModal, setShouldShowHazardousEmploymentModal] =
        useState<boolean>(false);

    const handleAccEmploymentChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target;
        const selectedEmployment = accEmployments.find(
            (employmentName) => employmentName.name === value
        );
        if (selectedEmployment === undefined) {
            return;
        }

        updateCoverableInformation({
            selectedEmployment
        });
        const riskyEmployment = parseInt(selectedEmployment?.hazardClass) > 5;
        setShouldShowHazardousEmploymentModal(riskyEmployment);
        setShowCallMeModal(riskyEmployment);
    };

    const onNext = (): void => {
        setIsLoading(true);
        const updatedCoverable: CoverableInformationToUpdateDTO = {
            adultAccident: {
                publicId,
                fixedId,
                employmentId: selectedEmployment?.publicId
            },
            productToUpdate: flavourName
        };

        void saveCoverableInformation(updatedCoverable).then(() => {
            track(`${flavourName}CoverableInformationEmployment`, [
                { employment: selectedEmployment?.name }
            ]);
            goNext();
            setIsLoading(false);
        });
    };

    return (
        <WizardPagetemplate
            onNext={onNext}
            onPrevious={goPrevious}
            disableNext={
                !isValid ||
                isLoading ||
                !selectedEmployment ||
                parseInt(selectedEmployment?.hazardClass) > 5 ||
                isRequestPending
            }
            disablePrevious={isLoading || isRequestPending}
            isNextLoading={isLoading || isRequestPending}
        >
            <CoverableDetailsTemplate
                header={
                    !insuranceForOtherPerson
                        ? getHippoText('DSS-COVERABLE-INFORMATION-STEP-EMPLOYMENT-SUBHEADER')
                        : getHippoText(
                              'DSS-COVERABLE-INFORMATION-STEP-EMPLOYMENT-SUBHEADER-FOR-OTHER',
                              {
                                  coverable_first_name: firstName ?? ''
                              }
                          )
                }
            >
                <TypeaheadField
                    label={getHippoText('DSS-COVERABLE-INFORMATION-STEP-EMPLOYMENT-SEARCHFIELD')}
                    value={selectedEmployment?.name}
                    onValidityChange={(isValid) => {
                        setIsValid(!!isValid);
                    }}
                    onChange={handleAccEmploymentChange}
                    options={accEmploymentsNames}
                    required
                    disabled={isLoading || isRequestPending}
                />
                <HelpLink
                    onClick={() => {
                        setShouldShowHazardousEmploymentModal(false);
                        setShowCallMeModal(true);
                    }}
                >
                    {getHippoText('DSS-COVERABLE-INFORMATION-STEP-EMPLOYMENT-NOT-ON-LIST')}
                </HelpLink>
            </CoverableDetailsTemplate>
            {showCallMeModal && (
                <CallMeModal
                    activityCode="dss_call_request"
                    showModal={showCallMeModal}
                    closeModal={() => {
                        setShowCallMeModal(false);
                    }}
                    title={getHippoText(
                        shouldShowHazardousEmploymentModal
                            ? 'DSS-EMPLOYMENTHAZARD-CALL-ME-HEADER'
                            : 'DSS-EMPLOYMENT-NOT-ON-LIST-CALL-ME-HEADER'
                    )}
                    description={getHippoText(
                        shouldShowHazardousEmploymentModal
                            ? 'DSS-EMPLOYMENTHAZARD-CALL-ME'
                            : 'DSS-EMPLOYMENT-NOT-ON-LIST-CALL-ME-SUBHEADER'
                    )}
                />
            )}
        </WizardPagetemplate>
    );
};

export default EmploymentStep;
